import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard, RoleGuard } from "../App";
import { StandardURL } from "../App";

const PropertyKYCPage = () => {
  const [propertyKycData, setPropertyKycData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPropertyKyc = async () => {
      const propertyId = localStorage.getItem("selectedPropertyId");
      if (!propertyId) {
        setError("Property ID not found in local storage. Please select a property.");
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/propertykyc/fetch/${propertyId}`,
          config
        );
        setPropertyKycData(response.data.response);
      } catch (err) {
        if (err.response?.data?.responseCode === 404 && err.response?.data?.response === "Property KYC not found!") {
          // Handle 404 case: No KYC data found
          setPropertyKycData(null);
        } else {
          // Handle other errors
          console.error("Error fetching property KYC data:", err);
          setError("Failed to fetch property KYC data. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyKyc();
  }, []);

  const handleAddPropertyKyc = () => {
    navigate("/landlord/addpropertykyc");
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-10 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">
            Property KYC Details 📝
          </h1>
          <hr className="my-6 border-gray-300" />
          {loading && (
            <div className="flex justify-center items-center h-48">
              <p className="text-lg text-gray-600">Loading property KYC data... ⏳</p>
            </div>
          )}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}
          {!loading && !error && (
            <>
              {propertyKycData ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <PropertyKycCard
                    title="Property KRA PIN"
                    value={propertyKycData.business_pin}
                  />
                  <PropertyKycCard
                    title="Tenancy Agreement Document"
                    value={propertyKycData.tenantagreementtemplate}
                  />
                  <PropertyKycCard
                    title="Your KRA Pin"
                    value={propertyKycData.krapin}
                  />
                  <PropertyKycCard
                    title="Utility Bills"
                    value={propertyKycData.utility_bills}
                  />
                  <PropertyKycCard
                    title="Your ID"
                    value={propertyKycData.landlordid}
                  />
                </div>
              ) : (
                <div className="text-center py-10">
                  <p className="text-xl text-gray-700 mb-6">
                    No KYC data found for this property.
                  </p>
                  <button
                    onClick={handleAddPropertyKyc}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Add Property KYC ➕
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

const PropertyKycCard = ({ title, value }) => {
  if (!value) return null; // Don't render the card if there's no value
  const isLink = value.startsWith("http://") || value.startsWith("https://");
  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out overflow-hidden">
      {isLink ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-6 group"
        >
          <p className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors duration-200">
            {title}
          </p>
          <p className="text-blue-500 text-sm truncate group-hover:underline">
            {value}
          </p>
          <span className="text-sm text-gray-500 mt-2 block">Click to view ↗️</span>
        </a>
      ) : (
        <div className="p-6">
          <p className="text-xl font-semibold text-gray-800 mb-2">{title}</p>
          <p className="text-gray-600 text-sm break-all">{value}</p>
        </div>
      )}
    </div>
  );
};

export default PropertyKYCPage;